import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore, hasMinimumRole, ROLES } from '../lib/auth';
import { supabase } from '../supabaseClient';
import { toast } from 'sonner';
import {
  dashboardAPI, taskAPI,
  notificationAPI, trainingAPI, briefingAPI, eodAPI, ehoAPI,
} from '../lib/api';
import notificationService from '../lib/notifications';
import {
  Building2, AlertTriangle, Clock,
  ArrowRight, Bell, ClipboardCheck, AlertCircle, Loader2,
  ChevronDown, ChevronUp, X,
  FileText, Zap, Check,
} from 'lucide-react';
import { Button } from '../components/ui/button';

// ─── Tiny pill stat ──────────────────────────────────────────────────────────
const StatPill = ({ label, value, color = 'default' }) => {
  const colorMap = {
    default: 'text-white',
    green: 'text-[#00E676]',
    red: 'text-[#FF3B30]',
    yellow: 'text-[#FFB300]',
    blue: 'text-[#2979FF]',
    orange: 'text-[#FF6600]',
  };
  return (
    <div className="flex flex-col items-center justify-center px-4 py-3 bg-[#1E1E1E] rounded-sm min-w-0">
      <span className={`text-xl font-bold font-heading ${colorMap[color]}`}>{value}</span>
      <span className="text-[#71717A] text-[11px] uppercase tracking-wide mt-0.5 text-center leading-tight">{label}</span>
    </div>
  );
};

// ─── Task row ─────────────────────────────────────────────────────────────────
const TaskRow = ({ task }) => {
  const isOverdue = task.status === 'overdue';
  const priorityDot = { high: 'bg-[#FF3B30]', medium: 'bg-[#FFB300]', low: 'bg-[#00E676]' };
  return (
    <div className={`flex items-start gap-3 px-4 py-3 border-l-2 ${isOverdue ? 'border-[#FF3B30] bg-[#FF3B30]/5' : 'border-[#3F3F46] bg-[#1E1E1E]'} rounded-r-sm`}>
      <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${priorityDot[task.priority] || priorityDot.medium}`} />
      <div className="flex-1 min-w-0">
        <p className="text-white text-sm font-medium truncate">{task.title}</p>
        <p className="text-[#71717A] text-xs mt-0.5 truncate">{task.venue_name} · Due {new Date(task.deadline).toLocaleString()}</p>
      </div>
      {isOverdue && (
        <span className="text-[10px] font-bold uppercase bg-[#FF3B30]/20 text-[#FF3B30] px-2 py-0.5 rounded shrink-0 self-center">
          Overdue
        </span>
      )}
    </div>
  );
};

// ─── Venue row (used in the lower card) ───────────────────────────────────────
const VenueRow = ({ venue, onSelect, active }) => {
  const score = venue.compliance_score;
  const color = score >= 90 ? 'text-[#00E676]' : score >= 70 ? 'text-[#FFB300]' : 'text-[#FF3B30]';
  return (
    <button
      onClick={() => onSelect(venue.id)}
      className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded-sm transition-colors text-left ${active ? 'bg-[#27272A] border border-[#FF6600]/30' : 'bg-[#1E1E1E] hover:bg-[#27272A]'}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <Building2 className="w-4 h-4 text-[#FF6600] shrink-0" />
        <span className="text-white text-sm font-medium truncate">{venue.name}</span>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className={`text-sm font-bold font-heading ${color}`}>{score}%</span>
        <ArrowRight className="w-3.5 h-3.5 text-[#52525B]" />
      </div>
    </button>
  );
};

// ─── Venue Switcher Dropdown ──────────────────────────────────────────────────
const VenueSwitcher = ({ venues = [], selectedId, onSelect }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const selected = venues.find((v) => v.id === selectedId) || venues[0];
  const score = selected?.compliance_score ?? null;
  const scoreColor =
    score === null ? 'text-[#71717A]'
    : score >= 90 ? 'text-[#00E676]'
    : score >= 70 ? 'text-[#FFB300]'
    : 'text-[#FF3B30]';

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (!venues.length) return null;

  return (
    <div ref={ref} className="relative w-full">
      {/* Trigger */}
      <button
        onClick={() => setOpen((p) => !p)}
        className="w-full flex items-center justify-between gap-3 px-4 py-2.5 bg-[#1E1E1E] border border-[#3F3F46] hover:border-[#FF6600]/50 rounded-sm transition-colors text-left group"
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <Building2 className="w-4 h-4 text-[#FF6600] shrink-0" />
          <span className="text-white text-sm font-medium truncate">
            {selected?.name || 'Select venue'}
          </span>
          {score !== null && (
            <span className={`text-xs font-bold font-heading ${scoreColor}`}>{score}%</span>
          )}
        </div>
        <ChevronDown
          className={`w-4 h-4 text-[#52525B] shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-[#1A1A1A] border border-[#3F3F46] rounded-sm shadow-xl overflow-hidden">
          {venues.map((v) => {
            const vScore = v.compliance_score;
            const vColor =
              vScore >= 90 ? 'text-[#00E676]'
              : vScore >= 70 ? 'text-[#FFB300]'
              : 'text-[#FF3B30]';
            const isActive = v.id === (selectedId || venues[0]?.id);
            return (
              <button
                key={v.id}
                onClick={() => { onSelect(v.id); setOpen(false); }}
                className={`w-full flex items-center justify-between gap-3 px-4 py-3 text-left transition-colors
                  ${isActive ? 'bg-[#27272A]' : 'hover:bg-[#222222]'}
                  border-b border-[#2A2A2A] last:border-0`}
              >
                <div className="flex items-center gap-2.5 min-w-0">
                  {isActive
                    ? <Check className="w-3.5 h-3.5 text-[#FF6600] shrink-0" />
                    : <Building2 className="w-3.5 h-3.5 text-[#52525B] shrink-0" />
                  }
                  <span className={`text-sm truncate ${isActive ? 'text-white font-semibold' : 'text-[#A1A1AA]'}`}>
                    {v.name}
                  </span>
                </div>
                <span className={`text-xs font-bold font-heading shrink-0 ${vColor}`}>{vScore}%</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── Accordion section ────────────────────────────────────────────────────────
const Accordion = ({ title, icon: Icon, badge, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-[#1E1E1E] border border-[#3F3F46] rounded-lg p-0 overflow-hidden">
      <button
        onClick={() => setOpen((p) => !p)}
        className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left hover:bg-[#27272A]/40 transition-colors"
      >
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-[#FF6600]" />}
          <span className="font-heading font-bold text-white uppercase tracking-tight text-sm">{title}</span>
          {badge !== undefined && badge > 0 && (
            <span className="ml-1 px-1.5 py-0.5 text-[10px] font-bold bg-[#FF6600] text-black rounded-full">{badge}</span>
          )}
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-[#52525B]" /> : <ChevronDown className="w-4 h-4 text-[#52525B]" />}
      </button>
      {open && <div className="px-5 pb-5 pt-1 space-y-3">{children}</div>}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const DashboardPage = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();

  const [stats, setStats] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [selectedVenueId, setSelectedVenueId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rtbGauge, setRtbGauge] = useState(null);
  const [gaugeLoading, setGaugeLoading] = useState(true);
  const [venueOps, setVenueOps] = useState(null);
  const [siteHealth, setSiteHealth] = useState(null);
  const [opsLoading, setOpsLoading] = useState(false);
  const [todaysBriefing, setTodaysBriefing] = useState(null);
  const [pushDismissed, setPushDismissed] = useState(false);
  const [pushSetup, setPushSetup] = useState({ checking: true, enabled: false, working: false, error: '' });
  const [compliancePackLoading, setCompliancePackLoading] = useState(false);

  const [activeForm, setActiveForm] = useState(null);
  const [savingStartBriefing, setSavingStartBriefing] = useState(false);
  const [savingEndReport, setSavingEndReport] = useState(false);
  const [startBriefingForm, setStartBriefingForm] = useState({
    manager_notes: '', operational_focus: '', staffing_notes: '',
    deliveries_expected_today: '', cleaning_priorities: '',
  });
  const [endReportForm, setEndReportForm] = useState({
    day_summary: '', stock_or_operational_issues: '',
    handover_notes_for_tomorrow: '', manager_sign_off: '',
  });

  const currentSiteId = selectedVenueId || stats?.venues?.[0]?.id || null;
  const now = new Date();
  const userCanRunDailyForms = [ROLES.MANAGER, ROLES.HEAD_CHEF].includes(user?.role);
  const currentHour = now.getHours();
  const startOfDayWindowOpen = currentHour >= 8 && currentHour < 11;
  const endOfDayWindowOpen = currentHour >= 22 || currentHour < 4;
  const selectedVenueName = stats?.venues?.find((v) => v.id === currentSiteId)?.name;
  const unreadCount = notifications.filter((n) => !n.is_read).length;
  const overdueTasks = tasks.filter((t) => t.status === 'overdue');
  const activeTasks = tasks.filter((t) => t.status !== 'overdue');

  const formatDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (isNaN(d)) return '—';
    return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
  };

  const fetchGauge = useCallback(async (statsData, venueId = null) => {
    const id = venueId || selectedVenueId || statsData?.venues?.[0]?.id;
    if (!id) { setRtbGauge(null); setGaugeLoading(false); return; }
    setGaugeLoading(true);
    try {
      const res = await trainingAPI.getGauge(id);
      setRtbGauge(res?.data || null);
    } catch { setRtbGauge(null); }
    finally { setGaugeLoading(false); }
  }, [selectedVenueId]);

  useEffect(() => {
    const load = async () => {
      try {
        const [statsRes, tasksRes, notifRes] = await Promise.all([
          dashboardAPI.getStats().catch(() => ({ data: { total_venues: 0, avg_compliance: 0, pending_tasks: 0, overdue_tasks: 0, today_submissions: 0, venues: [] } })),
          taskAPI.list(selectedVenueId || null, null).catch(() => ({ data: [] })),
          notificationAPI.list().catch(() => ({ data: [] })),
        ]);
        setStats(statsRes?.data || {});
        setTasks(tasksRes?.data || []);
        setNotifications(notifRes?.data || []);
        await fetchGauge(statsRes?.data || {}, selectedVenueId);
      } catch {
        setStats({ total_venues: 0, avg_compliance: 0, pending_tasks: 0, overdue_tasks: 0, today_submissions: 0, venues: [] });
        setTasks([]); setNotifications([]);
      } finally { setLoading(false); }
    };
    load();
  }, [selectedVenueId, fetchGauge]);

  useEffect(() => {
    const fetchOpsData = async () => {
      if (!currentSiteId) { setVenueOps(null); setSiteHealth(null); setTodaysBriefing(null); return; }
      setOpsLoading(true);
      try {
        const [venueOpsRes, siteHealthRes, briefingRes] = await Promise.all([
          dashboardAPI.getVenueOps(currentSiteId).catch(() => ({ data: null })),
          dashboardAPI.getSiteHealth(currentSiteId).catch(() => ({ data: null })),
          briefingAPI.getTodaysBriefing(currentSiteId).catch(() => ({ data: { briefing: null } })),
        ]);
        setVenueOps(venueOpsRes?.data || null);
        setSiteHealth(siteHealthRes?.data || null);
        setTodaysBriefing(briefingRes?.data?.briefing || null);
      } catch (error) {
        console.error('Ops dashboard fetch failed:', error);
      } finally { setOpsLoading(false); }
    };
    fetchOpsData();
  }, [currentSiteId]);

  // Phase 2: EHO Compliance Pack Generation
  const handleGenerateCompliancePack = async () => {
    if (!currentSiteId || compliancePackLoading) return;
    setCompliancePackLoading(true);
    try {
      const { data: docs, error } = await supabase
        .from('compliance_documents')
        .select('id')
        .eq('status', 'verified')
        .eq('venue_id', currentSiteId);

      if (error) throw error;

      if (!docs || docs.length === 0) {
        toast.error("No verified documents found. Please complete EHO verification in the Compliance Hub first.");
        setCompliancePackLoading(false);
        return;
      }

      toast.success(`Compiling ${docs.length} verified documents for EHO Pack...`);

      const response = await ehoAPI.generateCompliancePack(currentSiteId);
      const { pdf_base64: pdfBase64, filename } = response.data || {};
      if (!pdfBase64) return;

      const byteCharacters = atob(pdfBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let index = 0; index < byteCharacters.length; index += 1) {
        byteNumbers[index] = byteCharacters.charCodeAt(index);
      }
      const blob = new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename || 'RTB_Compliance_Pack.pdf';
      anchor.click();
      URL.revokeObjectURL(url);
      
      toast.success('EHO Compliance Pack downloaded successfully.');
    } catch (error) {
      console.error('Compliance pack generation failed:', error);
      toast.error('Failed to generate compliance pack.');
    } finally { 
      setCompliancePackLoading(false); 
    }
  };

  const handleSubmitStartBriefing = async () => {
    if (!currentSiteId || savingStartBriefing) return;
    if (!startBriefingForm.manager_notes?.trim() || !startBriefingForm.operational_focus?.trim()) return;
    setSavingStartBriefing(true);
    try {
      await briefingAPI.submitStartOfDay({ site_id: currentSiteId, ...startBriefingForm });
      setActiveForm(null);
      setStartBriefingForm({ manager_notes: '', operational_focus: '', staffing_notes: '', deliveries_expected_today: '', cleaning_priorities: '' });
      const briefingRes = await briefingAPI.getTodaysBriefing(currentSiteId);
      setTodaysBriefing(briefingRes?.data?.briefing || null);
      const siteHealthRes = await dashboardAPI.getSiteHealth(currentSiteId);
      setSiteHealth(siteHealthRes?.data || null);
    } catch (error) {
      console.error('Start of day briefing submission failed:', error);
    } finally { setSavingStartBriefing(false); }
  };

  const handleSubmitEndReport = async () => {
    if (!currentSiteId || savingEndReport) return;
    if (!endReportForm.day_summary?.trim() || !endReportForm.manager_sign_off?.trim()) return;
    setSavingEndReport(true);
    try {
      await eodAPI.submitEndOfDayReport({ site_id: currentSiteId, ...endReportForm });
      setActiveForm(null);
      setEndReportForm({ day_summary: '', stock_or_operational_issues: '', handover_notes_for_tomorrow: '', manager_sign_off: '' });
    } catch (error) {
      console.error('End of day report submission failed:', error);
    } finally { setSavingEndReport(false); }
  };

  const handleEnablePush = async () => {
    setPushSetup((prev) => ({ ...prev, working: true }));
    try {
      if (notificationService.isNative()) {
        const hasPermission = await notificationService.hasPermission();
        if (!hasPermission) await notificationService.requestPermission();
        await notificationService.syncStoredTokenWithBackend();
      } else {
        await notificationService.registerPushNotifications();
      }
      setPushSetup((prev) => ({ ...prev, enabled: true, working: false, error: '' }));
    } catch (error) {
      console.error('Push setup failed:', error);
      setPushSetup((prev) => ({ ...prev, working: false, error: error?.message || 'Failed to enable push' }));
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationAPI.markAllRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    } catch { /* silent */ }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-[#FF6600] animate-spin" />
      </div>
    );
  }

  const rtbScore = rtbGauge?.overall_score || 0;
  const rtbColor = rtbScore >= 90 ? '#00E676' : rtbScore >= 70 ? '#FFB300' : '#FF3B30';
  const healthColor = siteHealth?.color === 'green' ? '#00E676' : siteHealth?.color === 'amber' ? '#FFB300' : '#FF3B30';

  return (
    <div className="space-y-4 pb-8" data-testid="dashboard-page">

      {!pushSetup.checking && !pushSetup.enabled && !pushDismissed && (
        <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-[#1E1E1E] border border-[#3F3F46] rounded-sm text-sm">
          <span className="text-[#A1A1AA]">Enable push notifications to get live task alerts.</span>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={handleEnablePush} disabled={pushSetup.working} className="text-[#FF6600] hover:underline text-xs font-semibold">
              {pushSetup.working ? 'Enabling…' : 'Enable'}
            </button>
            <button onClick={() => setPushDismissed(true)} className="text-[#52525B] hover:text-white">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-white uppercase tracking-tight">Dashboard</h1>
            <p className="text-[#71717A] text-sm mt-0.5">
              {selectedVenueName || 'All venues'} · {now.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {currentSiteId && (
              <Button 
                className="bg-[#27272A] border border-[#3F3F46] text-white hover:bg-[#3F3F46] h-9 px-4 text-sm" 
                onClick={() => navigate('/compliance')}
              >
                EHO Vault
              </Button>
            )}
            <Button className="bg-[#FF6600] hover:bg-[#FF8533] text-white h-9 px-4 text-sm" onClick={() => navigate('/checklists/execute')}>
              <ClipboardCheck className="w-4 h-4 mr-1.5" />
              Start Checklist
            </Button>
            {currentSiteId && (
              <Button className="bg-[#27272A] border border-[#3F3F46] text-white hover:bg-[#3F3F46] h-9 px-4 text-sm" onClick={handleGenerateCompliancePack} disabled={compliancePackLoading}>
                <FileText className="w-4 h-4 mr-1.5" />
                {compliancePackLoading ? 'Generating…' : 'Compliance Pack'}
              </Button>
            )}
          </div>
        </div>

        {stats?.venues?.length > 0 && (
          <VenueSwitcher
            venues={stats.venues}
            selectedId={currentSiteId}
            onSelect={setSelectedVenueId}
          />
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatPill label="Avg Compliance" value={`${stats?.avg_compliance || 0}%`} color={stats?.avg_compliance >= 90 ? 'green' : stats?.avg_compliance >= 70 ? 'yellow' : 'red'} />
        <StatPill label="Open Tasks" value={stats?.pending_tasks || 0} color={stats?.overdue_tasks > 0 ? 'red' : 'yellow'} />
        <StatPill label="Today's Checks" value={stats?.today_submissions || 0} color="green" />
        <StatPill label="Venues" value={stats?.total_venues || 0} color="blue" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="bg-[#1E1E1E] border border-[#3F3F46] rounded-lg p-6" data-testid="rtb-gauge-card">
          {gaugeLoading ? (
            <div className="flex items-center justify-center h-20"><Loader2 className="w-5 h-5 text-[#FF6600] animate-spin" /></div>
          ) : (
            <>
              <div className="flex items-end justify-between gap-2 mb-3">
                <div>
                  <p className="text-[#A1A1AA] text-xs uppercase tracking-wide">RTB Score</p>
                  <p className="text-white text-3xl font-bold font-heading mt-0.5" style={{ color: rtbColor }}>{rtbScore}%</p>
                  <p className="text-[#52525B] text-xs mt-0.5">{selectedVenueName || 'Selected venue'}</p>
                </div>
                <div className="text-right text-xs text-[#71717A] space-y-1">
                  <p>Checklist <span className="text-white">{rtbGauge?.checklist_score || 0}%</span></p>
                  <p>Training <span className="text-white">{rtbGauge?.training_compliance || 0}%</span></p>
                  <p>Task Close <span className="text-white">{rtbGauge?.task_completion || 0}%</span></p>
                </div>
              </div>
              <div className="w-full h-2 rounded-full bg-[#27272A] overflow-hidden">
                <div className="h-full rounded-full transition-all duration-500" style={{ width: `${rtbScore}%`, backgroundColor: rtbColor }} />
              </div>
            </>
          )}
        </div>

        <div className={`bg-[#1E1E1E] rounded-lg p-6 border`} style={{ borderColor: opsLoading ? '#3F3F46' : healthColor + '55' }}>
          {opsLoading ? (
            <div className="flex items-center justify-center h-20"><Loader2 className="w-5 h-5 text-[#FF6600] animate-spin" /></div>
          ) : (
            <>
              <div className="flex items-end justify-between gap-2 mb-3">
                <div>
                  <p className="text-[#A1A1AA] text-xs uppercase tracking-wide">Site Health</p>
                  <p className="text-white text-3xl font-bold font-heading mt-0.5" style={{ color: healthColor }}>{siteHealth?.score ?? 0}</p>
                  <p className="text-[#52525B] text-xs mt-0.5">{siteHealth?.level || '—'}</p>
                </div>
                <div className="text-right text-xs text-[#71717A] space-y-1">
                  <p>Audit <span className="text-white">{siteHealth?.factors?.audit_pass_rate ?? 0}%</span></p>
                  <p>Tasks <span className="text-white">{siteHealth?.factors?.task_completion_rate ?? 0}%</span></p>
                  <p>Briefing <span className={siteHealth?.factors?.briefing_completed ? 'text-[#00E676]' : 'text-[#FFB300]'}>
                    {siteHealth?.factors?.briefing_completed ? 'Done' : 'Pending'}
                  </span></p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-[11px]">
                {[
                  { label: 'Due Today', val: venueOps?.tasks_due_today || 0 },
                  { label: 'Incidents', val: venueOps?.open_incidents || 0, warn: true },
                  { label: 'Equipment', val: venueOps?.broken_equipment || 0, warn: true },
                ].map(({ label, val, warn }) => (
                  <div key={label} className="bg-[#1E1E1E] rounded-sm px-2 py-1.5 text-center border border-[#3F3F46]">
                    <p className={`font-bold ${warn && val > 0 ? 'text-[#FF3B30]' : 'text-white'}`}>{val}</p>
                    <p className="text-[#71717A] leading-tight">{label}</p>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-[#1E1E1E] border border-[#3F3F46] rounded-lg p-6 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-base font-bold text-white uppercase tracking-tight flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-[#FF3B30]" />
              Overdue
              {overdueTasks.length > 0 && (
                <span className="text-[10px] font-bold bg-[#FF3B30] text-white px-1.5 py-0.5 rounded-full">{overdueTasks.length}</span>
              )}
            </h2>
            <Link to="/tasks" className="text-[#FF6600] text-xs hover:underline">View all →</Link>
          </div>
          {overdueTasks.length === 0 ? (
            <p className="text-[#52525B] text-sm py-2">No overdue tasks 🎉</p>
          ) : (
            <div className="space-y-2">
              {overdueTasks.slice(0, 4).map((t) => <TaskRow key={t.id} task={t} />)}
              {overdueTasks.length > 4 && (
                <Link to="/tasks" className="block text-center text-xs text-[#FF6600] hover:underline py-1">
                  +{overdueTasks.length - 4} more overdue tasks
                </Link>
              )}
            </div>
          )}
        </div>

        <div className="bg-[#1E1E1E] border border-[#3F3F46] rounded-lg p-6 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-base font-bold text-white uppercase tracking-tight flex items-center gap-2">
              <Clock className="w-4 h-4 text-[#FFB300]" />
              Active Tasks
            </h2>
            <Link to="/tasks" className="text-[#FF6600] text-xs hover:underline">View all →</Link>
          </div>
          {activeTasks.length === 0 ? (
            <p className="text-[#52525B] text-sm py-2">No active tasks right now.</p>
          ) : (
            <div className="space-y-2">
              {activeTasks.slice(0, 4).map((t) => <TaskRow key={t.id} task={t} />)}
              {activeTasks.length > 4 && (
                <Link to="/tasks" className="block text-center text-xs text-[#FF6600] hover:underline py-1">
                  +{activeTasks.length - 4} more tasks
                </Link>
              )}
            </div>
          )}
        </div>
      </div>

      {notifications.length > 0 && (
        <div className="bg-[#1E1E1E] border border-[#3F3F46] rounded-lg p-6 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-base font-bold text-white uppercase tracking-tight flex items-center gap-2">
              <Bell className="w-4 h-4 text-[#FF6600]" />
              Notifications
              {unreadCount > 0 && (
                <span className="text-[10px] font-bold bg-[#FF6600] text-black px-1.5 py-0.5 rounded-full">{unreadCount}</span>
              )}
            </h2>
            {unreadCount > 0 && (
              <button onClick={handleMarkAllRead} className="text-xs text-[#FF6600] hover:underline">Mark all read</button>
            )}
          </div>
          <div className="space-y-2 max-h-64 overflow-auto">
            {notifications.slice(0, 6).map((n) => (
              <div key={n.id} className={`flex items-start gap-2.5 p-3 rounded-sm ${n.is_read ? 'bg-[#1A1A1A]' : 'bg-[#27272A]'}`}>
                <AlertCircle className={`w-4 h-4 mt-0.5 shrink-0 ${n.notification_type === 'warning' ? 'text-[#FFB300]' : n.notification_type === 'error' ? 'text-[#FF3B30]' : 'text-[#2979FF]'}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium leading-snug">{n.title}</p>
                  <p className="text-[#71717A] text-xs mt-0.5 leading-snug">{n.message}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {todaysBriefing && (
        <Accordion title="Today's Briefing" icon={FileText} defaultOpen>
          <p className="text-[#71717A] text-xs mb-3">{formatDate(todaysBriefing?.briefing_date)} · {selectedVenueName}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { label: 'Manager Notes', val: todaysBriefing.manager_notes },
              { label: 'Operational Focus', val: todaysBriefing.operational_focus },
              { label: 'Cleaning Priorities', val: todaysBriefing.cleaning_priorities },
              { label: 'Deliveries Today', val: todaysBriefing.deliveries_expected_today },
            ].map(({ label, val }) => val && (
              <div key={label} className="bg-[#141414] border border-[#2A2A2A] rounded-sm p-3">
                <p className="text-[#71717A] text-[10px] uppercase tracking-wide mb-1">{label}</p>
                <p className="text-white text-sm whitespace-pre-wrap">{val}</p>
              </div>
            ))}
          </div>
        </Accordion>
      )}

      {userCanRunDailyForms && (
        <Accordion title="Daily Operations" icon={Zap}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="bg-[#141414] border border-[#2A2A2A] rounded-sm p-4 space-y-3">
              <div>
                <p className="text-white font-semibold text-sm">Start of Day Briefing</p>
                <p className="text-[#71717A] text-xs mt-0.5">Available 08:00 – 11:00</p>
              </div>
              {startOfDayWindowOpen ? (
                <>
                  <Button className="bg-[#FF6600] hover:bg-[#FF8533] text-white w-full text-sm" onClick={() => setActiveForm(activeForm === 'start' ? null : 'start')}>
                    {activeForm === 'start' ? 'Cancel' : 'Open Form'}
                  </Button>
                  {activeForm === 'start' && (
                    <div className="space-y-2 pt-1">
                      {[
                        { key: 'manager_notes', ph: 'Manager Notes *' },
                        { key: 'operational_focus', ph: "Today's Operational Focus *" },
                        { key: 'staffing_notes', ph: 'Staffing Notes' },
                        { key: 'deliveries_expected_today', ph: 'Deliveries Expected' },
                        { key: 'cleaning_priorities', ph: 'Cleaning Priorities' },
                      ].map(({ key, ph }) => (
                        <textarea key={key} className="flex min-h-[60px] w-full rounded-md border border-[#3F3F46] bg-[#121212] px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#FF6600]" placeholder={ph}
                          value={startBriefingForm[key]}
                          onChange={(e) => setStartBriefingForm((p) => ({ ...p, [key]: e.target.value }))} />
                      ))}
                      <Button className="bg-[#FF6600] hover:bg-[#FF8533] text-white w-full" disabled={savingStartBriefing} onClick={handleSubmitStartBriefing}>
                        {savingStartBriefing ? 'Submitting…' : 'Submit Briefing'}
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-[#52525B] text-xs">Available between 08:00 and 11:00.</p>
              )}
            </div>

            <div className="bg-[#141414] border border-[#2A2A2A] rounded-sm p-4 space-y-3">
              <div>
                <p className="text-white font-semibold text-sm">End of Day Report</p>
                <p className="text-[#71717A] text-xs mt-0.5">Available 22:00 – 04:00</p>
              </div>
              {endOfDayWindowOpen ? (
                <>
                  <Button className="bg-[#FF6600] hover:bg-[#FF8533] text-white w-full text-sm" onClick={() => setActiveForm(activeForm === 'end' ? null : 'end')}>
                    {activeForm === 'end' ? 'Cancel' : 'Open Form'}
                  </Button>
                  {activeForm === 'end' && (
                    <div className="space-y-2 pt-1">
                      {[
                        { key: 'day_summary', ph: 'Day Summary *' },
                        { key: 'stock_or_operational_issues', ph: 'Stock / Operational Issues' },
                        { key: 'handover_notes_for_tomorrow', ph: 'Handover Notes' },
                      ].map(({ key, ph }) => (
                        <textarea key={key} className="flex min-h-[60px] w-full rounded-md border border-[#3F3F46] bg-[#121212] px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#FF6600]" placeholder={ph}
                          value={endReportForm[key]}
                          onChange={(e) => setEndReportForm((p) => ({ ...p, [key]: e.target.value }))} />
                      ))}
                      <input className="flex h-10 w-full rounded-md border border-[#3F3F46] bg-[#121212] px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#FF6600]" placeholder="Manager Sign Off *"
                        value={endReportForm.manager_sign_off}
                        onChange={(e) => setEndReportForm((p) => ({ ...p, manager_sign_off: e.target.value }))} />
                      <Button className="bg-[#FF6600] hover:bg-[#FF8533] text-white w-full" disabled={savingEndReport} onClick={handleSubmitEndReport}>
                        {savingEndReport ? 'Submitting…' : 'Submit Report'}
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-[#52525B] text-xs">Available between 22:00 and 04:00.</p>
              )}
            </div>
          </div>
        </Accordion>
      )}

      {user?.role === ROLES.STAFF && (
        <div className="bg-[#1E1E1E] border border-[#3F3F46] rounded-lg p-6 space-y-3">
          <h3 className="font-heading text-base font-bold text-white uppercase tracking-tight">Quick Actions</h3>
          <div className="flex flex-wrap gap-2">
            <Link to="/staff-hub/holiday">
              <Button className="bg-[#27272A] border border-[#3F3F46] text-white hover:bg-[#3F3F46] text-sm">Request Holiday</Button>
            </Link>
            <Link to="/staff-hub/training">
              <Button className="bg-[#27272A] border border-[#3F3F46] text-white hover:bg-[#3F3F46] text-sm">View Training Docs</Button>
            </Link>
          </div>
        </div>
      )}

      {(!stats?.venues || stats.venues.length === 0) && (
        <div className="bg-[#1E1E1E] border border-[#3F3F46] rounded-lg text-center py-12">
          <Building2 className="w-12 h-12 text-[#3F3F46] mx-auto mb-4" />
          <h3 className="font-heading text-lg font-bold text-white uppercase mb-2">No Venues Assigned</h3>
          <p className="text-[#A1A1AA] mb-6 text-sm">You haven't been assigned to any venues yet.</p>
          {hasMinimumRole(user, ROLES.OPS) && (
            <Link to="/venues/new">
              <Button className="bg-[#FF6600] hover:bg-[#FF8533] text-white">Create Your First Venue</Button>
            </Link>
          )}
        </div>
      )}
    </div>
  );
};

export default DashboardPage;